/**
 * Account Service gRPC DTOs (Issue #314 - Type Unification)
 *
 * Type definitions for gRPC responses from Account Service (Service 24).
 * These use snake_case to match the proto definitions.
 */

import { z } from 'zod';

// ============================================================================
// USER RESPONSE (from account-service.proto UserResponse)
// ============================================================================

/**
 * gRPC User Response schema
 * Matches the proto UserResponse message structure (snake_case)
 */
export const UserGrpcResponseSchema = z.object({
  id: z.string(),
  business_id: z.string(),
  email: z.string(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  display_name: z.string().optional(),
  avatar_url: z.string().optional(),
  role: z.string(),
  status: z.string(),
  email_verified: z.boolean().optional(),
  phone_number: z.string().optional(),
  timezone: z.string().optional(),
  locale: z.string().optional(),
  last_login_at: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  // SSO Fields
  sso_provider: z.string().optional(),
  sso_subject: z.string().optional(),
  realm_id: z.string().optional(),
  // Manager/Org Structure
  manager_id: z.string().optional(),
  name: z.string().optional(),
  // MFA Status
  mfa_enabled: z.boolean().optional(),
  // Licensing (Issue #1223 GAP 8)
  nmls_id: z.string().optional(),
  // Licensed states (from active UserLicense issuingAuthority values) - Issue #1223 PR4
  licensed_states: z.array(z.string()).optional(),
});

export type UserGrpcResponse = z.infer<typeof UserGrpcResponseSchema>;

// ============================================================================
// LIST USERS RESPONSE
// ============================================================================

export const ListUsersGrpcResponseSchema = z.object({
  users: z.array(UserGrpcResponseSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});

export type ListUsersGrpcResponse = z.infer<typeof ListUsersGrpcResponseSchema>;

// ============================================================================
// USER PREFERENCES RESPONSE
// ============================================================================

export const UserPreferencesGrpcResponseSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  business_id: z.string(),
  theme: z.string().optional(),
  language: z.string().optional(),
  timezone: z.string().optional(),
  email_notifications: z.boolean().optional(),
  push_notifications: z.boolean().optional(),
  metadata: z.string().optional(), // JSON string
  created_at: z.string(),
  updated_at: z.string(),
});

export type UserPreferencesGrpcResponse = z.infer<typeof UserPreferencesGrpcResponseSchema>;

// ============================================================================
// REALM MEMBERSHIP RESPONSE
// ============================================================================

export const RealmMembershipGrpcResponseSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  realm_id: z.string(),
  role: z.string(),
  permissions: z.array(z.string()).optional(),
  joined_at: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type RealmMembershipGrpcResponse = z.infer<typeof RealmMembershipGrpcResponseSchema>;

export const ListRealmMembershipsGrpcResponseSchema = z.object({
  memberships: z.array(RealmMembershipGrpcResponseSchema),
  total: z.number().int(),
});

export type ListRealmMembershipsGrpcResponse = z.infer<typeof ListRealmMembershipsGrpcResponseSchema>;

// ============================================================================
// TEAM RESPONSE
// ============================================================================

export const TeamMemberGrpcSchema = z.object({
  id: z.string(),
  team_id: z.string(),
  user_id: z.string(),
  role: z.string(),
  joined_at: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  user_name: z.string().optional(),
  user_email: z.string().optional(),
  user_avatar_url: z.string().optional(),
});

export type TeamMemberGrpc = z.infer<typeof TeamMemberGrpcSchema>;

export const TeamGrpcResponseSchema = z.object({
  id: z.string(),
  business_id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  color: z.string().optional(),
  icon: z.string().optional(),
  manager_id: z.string().optional(),
  is_active: z.boolean(),
  created_by: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  member_count: z.number().int().optional(),
  members: z.array(TeamMemberGrpcSchema).optional(),
});

export type TeamGrpcResponse = z.infer<typeof TeamGrpcResponseSchema>;

export const ListTeamsGrpcResponseSchema = z.object({
  teams: z.array(TeamGrpcResponseSchema),
  total: z.number().int(),
});

export type ListTeamsGrpcResponse = z.infer<typeof ListTeamsGrpcResponseSchema>;
